import type { AdminApiBootResponse } from "@repo/app-types/api/boot";
import type {
  UserMcpOAuthConsentResponse,
  UserNeedsOnboardingResponse,
  UserResolutionResponse,
  UserResolvedResponse,
} from "@repo/app-types/api/user";
import { api } from "../redux.js";

/** Account-resolution POST endpoints return a resolved (or onboarding) user. */
type UserJoinedResponse = UserResolvedResponse | UserNeedsOnboardingResponse;

const userApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Keyed on non-secret Supabase login metadata (not sent in the request) so
    // account resolution isn't reused after a fresh authentication. The
    // invitation token from an `/accept-invitation?t=...` deep link is passed
    // along so the resolution reflects the invitation the user is acting on.
    user: builder.query<
      UserResolutionResponse,
      { loginCacheKey: string; invitationToken: string | null }
    >({
      query: ({ invitationToken }) => ({
        method: "GET",
        url: "/user",
        params: invitationToken ? { t: invitationToken } : undefined,
      }),
    }),
    // Keyed on the same login value as `user`, so a fresh authentication can't
    // reuse a previous login's cached boot payload.
    boot: builder.query<AdminApiBootResponse, string>({
      query: () => ({ method: "GET", url: "/boot" }),
    }),
    // Keyed on the same login value as `user`. Needs only a Supabase session,
    // so it answers before account resolution.
    mcpOAuthConsent: builder.query<UserMcpOAuthConsentResponse, string>({
      query: () => ({ method: "GET", url: "/user/mcp-oauth-consent" }),
    }),
    joinViaSso: builder.mutation<UserJoinedResponse, void>({
      query: () => ({ method: "POST", url: "/user/sso" }),
    }),
    acceptInvitation: builder.mutation<
      UserJoinedResponse,
      { token?: string | null }
    >({
      query: ({ token }) => ({
        method: "POST",
        url: "/user/accept-invitation",
        params: token ? { t: token } : undefined,
      }),
    }),
    joinDomain: builder.mutation<UserJoinedResponse, void>({
      query: () => ({ method: "POST", url: "/user/join-domain" }),
    }),
  }),
});

export const {
  useUserQuery,
  useBootQuery,
  useMcpOAuthConsentQuery,
  useJoinViaSsoMutation,
  useAcceptInvitationMutation,
  useJoinDomainMutation,
} = userApi;
